import { PageId } from "../types";
import type { EffectiveSubject } from "../hooks/useEffectiveProtection";
import type { PolicyTab } from "./pageRouting";
import type { ConsoleKind } from "./consoleNav";
import { ProtectedModel } from "./protectedModel";

/**
 * ENG-4063 — where the pages retired under `protectedModel: 2` send an old link, in the organization's
 * console:
 *
 * - `#effective?member=<id>` → that member's detail page (`#user?id=<id>`), which lists their Protected
 *   sites with a reason each. To Protected sites instead when this admin cannot open member detail.
 * - `#effective?group=<id>` → Protected sites, scoped to that group.
 * - `#effective` → Protected sites.
 * - `#policy?tab=sites` → Protected sites: Policy has no Sites & categories tab under model 2.
 *
 * An IT provider's operator gets the same editor for the client (Sean, 2026-10-28), so their old
 * links land the same way.
 *
 * Null when nothing redirects: model 1, a console not yet known, the member console, any other page.
 */
export interface Model2Redirect {
  page: PageId;
  userId?: string;
  sitesScope?: string;
}

export function model2Redirect(
  page: PageId,
  opts: {
    model: ProtectedModel | null;
    kind: ConsoleKind;
    subject: EffectiveSubject | null;
    policyTab: PolicyTab | null;
    /** The admin can open a member's detail page (`canManageMembers && canViewMemberActivity`). */
    canOpenMember: boolean;
    /** An IT provider's operator: since Sean's 2026-10-28 call they land as an admin does. */
    provider?: boolean;
  },
): Model2Redirect | null {
  if (opts.model !== 2 || opts.kind !== "org") return null;
  if (page === "effective") {
    if (opts.subject?.kind === "member" && opts.canOpenMember) return { page: "user", userId: opts.subject.id };
    if (opts.subject?.kind === "group") return { page: "sites", sitesScope: opts.subject.id };
    return { page: "sites" };
  }
  if (page === "policy" && opts.policyTab === "sites") return { page: "sites" };
  return null;
}

/**
 * ENG-4063 review: which Protected sites page the layout renders.
 *
 * - `legacy`: today's SecureSitesPage. Model 1 (an IT provider's operator keeps the client's custom
 *   sites there, SecureSitesPage `providerView`).
 * - `loading`: the model is not known yet. Nothing renders but a spinner, so model-1 copy never
 *   flashes under model 2 (contexts/ProtectedModelContext).
 * - `editor`: model 2, the org console, an admin of the viewed organization, or an IT provider's
 *   operator in a client (Sean, 2026-10-28; their tier decides what they can write).
 * - `member`: model 2, anyone else (a member, a personal account, a lapsed admin).
 */
export type SitesPage = "legacy" | "loading" | "editor" | "member";

export function sitesPageFor(opts: { model: ProtectedModel | null; provider: boolean; kind: ConsoleKind; isTenantAdmin: boolean }): SitesPage {
  if (opts.model === null) return opts.provider ? "legacy" : "loading";
  if (opts.model === 1) return "legacy";
  return opts.kind === "org" && opts.isTenantAdmin ? "editor" : "member";
}
